import React, { useState } from 'react';
import { FaPlus, FaTrash } from 'react-icons/fa';
import { mockPolicies } from '../../../data/mockScenarioData';

const RegulatoryTab = ({ data, updateData }) => {
  const [selectedPolicies, setSelectedPolicies] = useState(data.selectedPolicies || []);
  const [customPolicies, setCustomPolicies] = useState([]);
  const [showAddCustom, setShowAddCustom] = useState(false);

  const handlePolicyToggle = (policyId) => {
    const newSelected = selectedPolicies.includes(policyId)
      ? selectedPolicies.filter(id => id !== policyId)
      : [...selectedPolicies, policyId];
    
    setSelectedPolicies(newSelected);
    updateData({ selectedPolicies: newSelected });
  };

  const addCustomPolicy = (policy) => {
    const newCustom = [...customPolicies, policy];
    setCustomPolicies(newCustom);
    setShowAddCustom(false);
  };

  const removeCustomPolicy = (index) => {
    const newCustom = customPolicies.filter((_, i) => i !== index);
    setCustomPolicies(newCustom);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
        <div className="mb-3">
          <h3 className="text-xl font-bold text-gray-800 font-ui">Government Policy Settings</h3>
        </div>
        <p className="text-sm text-gray-700">
          Configure policy levers including incentives, mandates, and regulatory requirements that affect EV adoption and infrastructure planning.
        </p>
      </div>

      {/* Policy Library */}
      <div className="bg-white rounded-lg p-6 border border-gray-200">
        <h4 className="text-lg font-semibold text-gray-800 mb-4 font-ui">
          Policy Library
        </h4>
        
        <div className="space-y-3">
          {mockPolicies.map(policy => {
            const isSelected = selectedPolicies.includes(policy.id);
            
            return (
              <div
                key={policy.id}
                className={`border-2 rounded-lg p-4 transition-all cursor-pointer ${
                  isSelected
                    ? 'border-gray-900 bg-white'
                    : 'border-gray-200 bg-white hover:border-gray-400'
                }`}
                onClick={() => handlePolicyToggle(policy.id)}
              >
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handlePolicyToggle(policy.id)}
                    className="mt-1 mr-3"
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="font-bold text-gray-800">{policy.name}</h5>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        policy.type === 'mandate' 
                          ? 'bg-gray-200 text-gray-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {policy.type.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{policy.description}</p>
                    
                    {isSelected && (
                      <div className="bg-gray-50 rounded p-3 border border-gray-300">
                        <p className="text-xs font-semibold text-gray-700 mb-2">Policy Details:</p>
                        
                        {policy.details.zevTargets && (
                          <div className="mb-2">
                            <p className="text-xs text-gray-600 font-semibold">ZEV Targets:</p>
                            <div className="flex gap-3 mt-1">
                              {policy.details.zevTargets.map((target, idx) => (
                                <span key={idx} className="text-xs bg-gray-100 px-2 py-1 rounded">
                                  {target.year}: {target.percentage}%
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {policy.details.incentives && (
                          <div>
                            <p className="text-xs text-gray-600 font-semibold">Incentives:</p>
                            <div className="grid grid-cols-2 gap-2 mt-1">
                              {Object.entries(policy.details.incentives).map(([key, value]) => (
                                <div key={key} className="text-xs">
                                  <span className="text-gray-600 capitalize">
                                    {key.replace(/([A-Z])/g, ' $1').trim()}:
                                  </span>
                                  <span className="font-semibold text-gray-800 ml-1">
                                    ${value.toLocaleString()}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Custom Policies */}
      <div className="bg-white rounded-lg p-6 border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-gray-800 font-ui">Custom Policies</h4>
          <button
            onClick={() => setShowAddCustom(!showAddCustom)}
            className="flex items-center px-4 py-2 bg-gray-800 hover:bg-gray-900 text-white rounded-lg transition-colors text-sm font-semibold"
          >
            <FaPlus className="mr-2" />
            Add Custom Policy
          </button>
        </div>

        {showAddCustom && (
          <div className="mb-4 p-4 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
            <CustomPolicyForm onAdd={addCustomPolicy} onCancel={() => setShowAddCustom(false)} />
          </div>
        )}

        {customPolicies.length > 0 ? (
          <div className="space-y-3">
            {customPolicies.map((policy, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="flex items-start justify-between">
                  <div>
                    <h5 className="font-bold text-gray-800">{policy.name}</h5>
                    <p className="text-sm text-gray-600">{policy.description}</p>
                  </div>
                  <button
                    onClick={() => removeCustomPolicy(index)}
                    className="text-red-600 hover:text-red-800 transition-colors"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 text-center py-8">
            No custom policies added yet. Click "Add Custom Policy" to create one.
          </p>
        )}
      </div>
    </div>
  );
};

const CustomPolicyForm = ({ onAdd, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    type: 'incentive',
    description: '',
    purchaseRebate: '',
    infrastructureRebate: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onAdd(formData);
    setFormData({
      name: '',
      type: 'incentive',
      description: '',
      purchaseRebate: '',
      infrastructureRebate: ''
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1">Policy Name</label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
          required
        />
      </div>
      
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1">Type</label>
        <select
          value={formData.type}
          onChange={(e) => setFormData({ ...formData, type: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
        >
          <option value="incentive">Incentive</option>
          <option value="mandate">Mandate</option>
        </select>
      </div>
      
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
          rows="2"
          required
        />
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Purchase Rebate ($)</label>
          <input
            type="number"
            value={formData.purchaseRebate}
            onChange={(e) => setFormData({ ...formData, purchaseRebate: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Infrastructure Rebate ($)</label>
          <input
            type="number"
            value={formData.infrastructureRebate}
            onChange={(e) => setFormData({ ...formData, infrastructureRebate: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
          />
        </div>
      </div>
      
      <div className="flex gap-2 pt-2">
        <button
          type="submit"
          className="flex-1 px-4 py-2 bg-gray-800 hover:bg-gray-900 text-white rounded-lg transition-colors font-semibold"
        >
          Add Policy
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg transition-colors font-semibold"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default RegulatoryTab;
